import React, { useMemo, useState } from 'react';
import {
    useFloating,
    autoUpdate,
    offset as floatingOffset,
    flip,
    shift,
    size,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    FloatingFocusManager,
    type Placement,
    type MiddlewareState,
} from '@floating-ui/react';
import { useMountEffect } from '../../hooks/useMountEffect';
import { acquireZIndex, releaseZIndex } from '../../utils/zIndexStack';

export interface PopoverProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The element the popover anchors to. Must be stable across renders while open. */
    anchor: HTMLElement | null;
    placement?: Placement;
    /** Pixel gap between anchor and popover. Default 8. */
    offsetPx?: number;
    /** When true, popover min-width matches the anchor width (select-style). */
    matchAnchorWidth?: boolean;
    /** When true, focus is trapped inside the popover and restored on close. Default true. */
    trapFocus?: boolean;
    /** ARIA role for the floating element. Default "dialog". */
    role?: 'dialog' | 'menu' | 'listbox' | 'tooltip';
    children: React.ReactNode;
    className?: string;
}

const Popover: React.FC<PopoverProps> = (props) => {
    if (!props.open) {
        return null;
    }
    return <PopoverContent {...props} />;
};

const PopoverContent: React.FC<PopoverProps> = ({
    open,
    onOpenChange,
    anchor,
    placement = 'bottom-start',
    offsetPx = 8,
    matchAnchorWidth = false,
    trapFocus = true,
    role: roleProp = 'dialog',
    children,
    className,
}) => {
    const [zIndex] = useState<number>(() => acquireZIndex());

    useMountEffect(() => () => {
        releaseZIndex();
    });

    const middleware = useMemo(() => {
        const m = [floatingOffset(offsetPx), flip({ padding: 8 }), shift({ padding: 8 })];
        if (matchAnchorWidth) {
            m.push(
                size({
                    apply({ rects, elements }: MiddlewareState) {
                        elements.floating.style.minWidth = `${rects.reference.width}px`;
                    },
                }),
            );
        }
        return m;
    }, [offsetPx, matchAnchorWidth]);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange,
        placement,
        middleware,
        whileElementsMounted: autoUpdate,
        // Controlled reference: positioning stays correct even if `anchor`
        // resolves after mount. A one-shot setReference on mount would leave
        // the popover stuck at the viewport's top-left when the trigger ref
        // isn't populated yet at that instant.
        elements: { reference: anchor },
        // Position with top/left, NOT a transform. Floating UI defaults to a
        // `transform: translate(...)` for positioning, but consumers animate
        // the floating element's own `transform` on entry (e.g. `.bf-pop`'s
        // bf-pop-in keyframes end on `transform: none`). A CSS animation wins
        // over the inline transform, clobbering the position and dumping the
        // popover at the viewport's top-left. Using top/left keeps the two
        // concerns separate so entry animations can own `transform`.
        transform: false,
    });

    // Floating UI's useDismiss handles outside-press AND escape with capture-phase
    // by default — when a Popover sits above a Modal, this Popover's dismiss
    // wins, and the parent Modal's Escape listener does not fire.
    const dismiss = useDismiss(context, {
        escapeKey: true,
        outsidePress: true,
        bubbles: false,
    });
    const role = useRole(context, { role: roleProp });
    const { getFloatingProps } = useInteractions([dismiss, role]);

    const popoverNode = (
        <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex }}
            className={className}
            {...getFloatingProps()}
        >
            {children}
        </div>
    );

    return (
        <FloatingPortal>
            {trapFocus ? (
                <FloatingFocusManager context={context} modal={false}>
                    {popoverNode}
                </FloatingFocusManager>
            ) : (
                popoverNode
            )}
        </FloatingPortal>
    );
};

export default Popover;
